import { call, put, select, takeLatest } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { PulsaraConstants } from '../store/pulsara.constants'
import { PulsaraService } from '../services/pulsara.service'
import { IPulsaraDetailsRequest, IPulsaraDetailsResponse } from '../store/pulsara.types'
import { getPulsaraSelector } from '../selectors'
import i18n from '../../../i18n'
import { setCurrentStep, setPulsaraDetails } from '../store/pulsara.actions'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setPatientSearchData } from '../../patientSearch/store/patientSearch.actions'
import { initializePatientConflictFromPCR } from './initializePatientConflictSelections.saga'
import { isOffline } from '../../../utilities'
import { STEP_IDS } from '../constants'
import {
  setErrorMessage,
  setErrorToastDuration,
  showToast,
} from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { resetLoader, showLoader } from '../../../components/loader/store/loader.actions'
import Logger from '../../../services/logger.service'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPcrIdSelector } from '../../home/selectors/getPcrId.selector'
import { IPcr } from '../../pcr/store/pcr.types'

export function* fetchPulsaraDetails() {
  if (isOffline()) {
    yield put(setErrorMessage(i18n.t('pulsara.errors.offline')))
    yield put(setErrorToastDuration(2000))
    yield put(showToast(true))
    return
  }

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const { selectedPulsaraId } = yield select(getPulsaraSelector)
  const { FORCE_PT_SEARCH } = yield select(getServiceConfigSelector)
  if (!selectedPulsaraId) {
    return
  }

  yield put(showLoader({ isOpen: true, message: i18n.t('pulsara.loading.pulsaraDetails') }))

  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pcrID: string = yield select(getPcrIdSelector)

    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    const pulsaraService = new PulsaraService(authenticationJwt)
    const hasImportedCadData = !!pcrDoc.cadImport?.importedCadData

    const request: IPulsaraDetailsRequest = {
      pulsaraId: selectedPulsaraId,
    }

    const response: IPulsaraDetailsResponse = yield call([pulsaraService, pulsaraService.fetchPulsaraDetails], request)

    if (response.status === 200) {
      const { patient, situation, vitals, labs, caseType, createdAt } = response.data

      yield put(
        setPulsaraDetails({
          caseType,
          createdAt,
          patient,
          situation,
          vitals: vitals ?? [],
          labs: labs ?? [],
          attachmentList: [],
          attachments: [],
        })
      )
      Logger.info(`Pulsara: fetchPulsaraDetails success for pulsaraId: ${selectedPulsaraId}`)
      const alreadyHasPatient =
        !!pcrDoc?.ptid || !!pcrDoc?.patient?.patientInformation?.newPatient || hasImportedCadData
      if (FORCE_PT_SEARCH === 1 && !alreadyHasPatient) {
        const patientSearchData = {
          firstName: patient?.patientDemographics?.firstName || '',
          lastName: patient?.patientDemographics?.lastName || '',
          dOB: patient?.patientDemographics?.dOB || '',
          sSN: '',
        }

        yield put(setPatientSearchData(patientSearchData))
      }

      if (FORCE_PT_SEARCH === 0 || (FORCE_PT_SEARCH === 1 && alreadyHasPatient)) {
        yield call(initializePatientConflictFromPCR)
        yield put(setCurrentStep(STEP_IDS.PATIENT_MATCH))
      } else {
        yield put(setCurrentStep(STEP_IDS.PATIENT_MATCH))
      }
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage = i18n.t('pulsara.errors.fetchDetails')
      }
      yield put(handleApiError(response))
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: i18n.t('pulsara.errors.fetchDetailsFailed', { cause }),
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  } finally {
    yield put(resetLoader())
  }
}

export function* fetchPulsaraDetailsSaga() {
  yield takeLatest([PulsaraConstants.FETCH_PULSARA_DETAILS], performWithMutexHOG, fetchPulsaraDetails)
}
